import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  CircularProgress,
  Typography,
  Box,
  TextField,
  IconButton,
  Checkbox,
  useTheme,
  alpha,
  Tooltip,
  Paper,
  Chip,
  Button,
  Snackbar,
  Alert,
  Modal,
  Fade,
  Backdrop,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFetchObjects } from '../../hooks/objectHooks';
import { ObjectService } from '../../api/ObjectService';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useFetchPendingDocuments } from '../../hooks/useFetchObject';
import { AssistantService } from '../../api/AssistantService';
import { RequestFlag } from '../../api/protosCompiled/assistants/assistants_pb';
import { PDFPreview } from './PDFPreview';
import { useQueryClient } from 'react-query';
import { DocumentManagerActiveUploads } from './DocumentManagerActiveUploads';
import { Inference } from '../../hooks/inferenceHooks';

export const DocumentManager = () => {
  const theme = useTheme();
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const queryClient = useQueryClient();
  const {
    data: documents = [],
    isLoading,
    error,
    refetchObjects,
    documentStatus,
    startPollingForNewDocuments,
  } = useFetchObjects();

  const { data: healthCheckData } = Inference.useHealthCheck();

  useEffect(() => {
    console.log('healthCheckData', healthCheckData);
  }, [healthCheckData]);

  // Auto-polling state for more frequent refreshes
  const [autoPolling, setAutoPolling] = useState(false);
  const autoPollingRef = useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 5000; // 5 seconds

  // Start auto polling as soon as the component mounts
  useEffect(() => {
    // Start immediate polling for new documents
    startPollingForNewDocuments();

    // Also set up our own frequent polling mechanism
    setAutoPolling(true);

    return () => {
      // Cleanup polling on unmount
      if (autoPollingRef.current) {
        clearTimeout(autoPollingRef.current);
      }
      setAutoPolling(false);
    };
  }, [startPollingForNewDocuments]);

  // Handle auto polling logic
  useEffect(() => {
    if (autoPolling) {
      const poll = () => {
        refetchObjects();
        // Schedule next poll
        autoPollingRef.current = setTimeout(poll, POLLING_INTERVAL);
      };

      // Start initial poll
      autoPollingRef.current = setTimeout(poll, POLLING_INTERVAL);

      return () => {
        if (autoPollingRef.current) {
          clearTimeout(autoPollingRef.current);
        }
      };
    }
  }, [autoPolling, refetchObjects]);

  // Update the document auto refresh logic when the active component changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Immediately refresh when tab becomes visible
        refetchObjects();
      }
    };

    // Add visibility change listener to refresh when the user comes back to the tab
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchObjects]);

  const { data: pendingDocuments = [], refetch: refetchPendingDocuments } =
    useFetchPendingDocuments(selectedProject.id);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [updatedName, setUpdatedName] = useState<string>('');
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  const cachedPreviews = useRef<Record<string, string>>({});
  const transformRefs = useRef<Record<string, ReactZoomPanPinchRef>>({});

  // Keep stable order by using a useRef to store original document order
  const originalOrderRef = useRef<string[]>([]);

  const [renameProcessing, setRenameProcessing] = useState(false);

  // New state for rename suggestions
  const [renameSuggestions, setRenameSuggestions] = useState<
    Array<{
      sheet_name: string;
      sheet_number: string;
      gcs_object_name: string;
    }>
  >([]);
  const [applyingRenames, setApplyingRenames] = useState(false);

  // Add a local documents state to track optimistic updates
  const [localDocuments, setLocalDocuments] = useState<any[]>([]);

  // Add a ref to track pending optimistic updates
  const pendingUpdatesRef = useRef<Record<string, string>>({});

  // Initialize localDocuments when documents are loaded
  useEffect(() => {
    if (documents && documents.length > 0) {
      setLocalDocuments(documents);
    }
  }, [documents]);

  // Add notification state for Snackbar
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Add state for delete confirmation dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    itemsToDelete: 0,
    hasTakeoffs: false,
    isProcessing: false,
  });

  // Function to show notifications
  const showNotification = useCallback(
    (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => {
      setNotification({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  // Function to close notification
  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Manual refresh function
  const handleManualRefresh = useCallback(() => {
    showNotification('Refreshing documents...', 'info');
    refetchObjects();
  }, [refetchObjects, showNotification]);

  // Update the handleRenameSelected function to use the notification system
  const handleRenameSelected = useCallback(async () => {
    const gcsObjectNames = selectedDocumentIds.map(
      id => documents?.find(doc => doc.id === id)?.gcsobjectname
    );

    if (!gcsObjectNames.length) return;

    setRenameProcessing(true);
    setRenameSuggestions([]);
    showNotification(
      'AI is analyzing your documents to create smart rename suggestions...',
      'info'
    );

    try {
      let responseText = '';
      await AssistantService.streamMessage({
        usermessage: 'Scan the documents to perform your task',
        projectid: selectedProject.id,
        estimateid: selectedEstimate?.estimateid || '',
        requestflag: RequestFlag.REQUEST_FLAG_RENAME_DOCUMENT,
        gcsobjectnames: gcsObjectNames.filter(name => name !== undefined) as string[],
        onMessage: (chunk: string) => {
          responseText += chunk;

          // Try to parse JSON from the response
          try {
            // This regex finds array-like structures in the response without using 's' flag
            const jsonMatch = responseText.match(/\[\s*\{[\s\S]*\}\s*\]/);
            if (jsonMatch) {
              const jsonStr = jsonMatch[0];
              const suggestions = JSON.parse(jsonStr);

              if (
                Array.isArray(suggestions) &&
                suggestions.length > 0 &&
                'sheet_name' in suggestions[0] &&
                'sheet_number' in suggestions[0] &&
                'gcs_object_name' in suggestions[0]
              ) {
                setRenameSuggestions(suggestions);
                showNotification(
                  `AI analyzed your documents and found ${suggestions.length} rename suggestions`,
                  'success'
                );

                // Initialize all suggestions as approved by default
                const initialApprovals: Record<string, boolean> = {};
                suggestions.forEach(suggestion => {
                  initialApprovals[suggestion.gcs_object_name] = true;
                });
              }
            }
          } catch (e) {
            // Not valid JSON yet or not in expected format, continue accumulating chunks
            console.log('Error parsing JSON:', e);
          }
        },
      });

      // After completion, refresh the documents list if we didn't get valid JSON
      if (renameSuggestions.length === 0) {
        setTimeout(() => {
          refetchObjects();
          showNotification(
            'AI document analysis completed. No rename suggestions found.',
            'success'
          );
        }, 1000);
      }
    } catch (error) {
      console.error('Error processing rename operation:', error);
      showNotification('Error while analyzing documents with AI. Please try again.', 'error');
    } finally {
      setRenameProcessing(false);
    }
  }, [
    selectedDocumentIds,
    documents,
    selectedProject.id,
    selectedEstimate,
    refetchObjects,
    renameSuggestions.length,
    showNotification,
  ]);

  // Add a function to globally refresh document lists across components
  const refreshDocumentGlobally = useCallback(() => {
    // Invalidate the availableObjects query to refresh the ToolBar component
    queryClient.invalidateQueries(['availableObjects', selectedProject.id]);
    // Refetch the documents in this component
    refetchObjects();
    showNotification('Document list refreshed', 'success');
  }, [queryClient, selectedProject.id, refetchObjects, showNotification]);

  // Update approveAllSuggestions to refresh documents globally
  const approveAllSuggestions = useCallback(async () => {
    setApplyingRenames(true);
    showNotification('Applying all rename suggestions...', 'info');

    try {
      // Apply optimistic updates for all suggestions at once
      const updatesToApply: Record<string, string> = {};

      // Prepare all optimistic updates
      for (const suggestion of renameSuggestions) {
        const document = localDocuments?.find(
          doc => doc.gcsobjectname === suggestion.gcs_object_name
        );
        if (document) {
          const newName = `${suggestion.sheet_number} - ${suggestion.sheet_name}`;
          updatesToApply[document.id] = newName;
        }
      }

      // Apply all optimistic updates at once
      setLocalDocuments(prevDocs =>
        prevDocs.map(doc => {
          if (updatesToApply[doc.id]) {
            return { ...doc, objectname: updatesToApply[doc.id] };
          }
          return doc;
        })
      );

      // Save to pending updates
      Object.keys(updatesToApply).forEach(docId => {
        pendingUpdatesRef.current[docId] = updatesToApply[docId];
      });

      // Clear suggestions immediately for better UX
      setRenameSuggestions([]);

      // Actually perform the updates on the server
      for (const [docId, newName] of Object.entries(updatesToApply)) {
        await ObjectService.updateObjectName(docId, newName);
        // Remove from pending as each completes
        delete pendingUpdatesRef.current[docId];
      }

      showNotification('All rename suggestions applied successfully', 'success');

      // Refresh document lists across components
      refreshDocumentGlobally();
    } catch (error) {
      console.error('Error applying rename suggestions:', error);
      showNotification('Error applying some rename suggestions', 'error');
      // Clear pending updates
      pendingUpdatesRef.current = {};
      // Refresh to get actual state
      refetchObjects();
    } finally {
      setApplyingRenames(false);
    }
  }, [
    renameSuggestions,
    localDocuments,
    refetchObjects,
    showNotification,
    refreshDocumentGlobally,
  ]);

  // Update rejectAllSuggestions to use the notification system
  const rejectAllSuggestions = useCallback(() => {
    // Simply clear all suggestions without applying any changes
    setRenameSuggestions([]);
    showNotification('All rename suggestions rejected', 'info');
  }, [showNotification]);

  // Update original order when documents load or change
  useEffect(() => {
    if (documents && documents.length > 0 && originalOrderRef.current.length === 0) {
      originalOrderRef.current = documents.map(doc => doc.id);
    }
  }, [documents]);

  // Sort documents to maintain stable order
  const sortedDocuments = useMemo(() => {
    if (!localDocuments || localDocuments.length === 0) return [];

    // Create a lookup map for original positions
    const positionMap: Record<string, number> = {};
    originalOrderRef.current.forEach((id, index) => {
      positionMap[id] = index;
    });

    // Add any new documents that weren't in the original order
    localDocuments.forEach(doc => {
      if (!(doc.id in positionMap)) {
        positionMap[doc.id] = Object.keys(positionMap).length;
        originalOrderRef.current.push(doc.id);
      }
    });

    // Sort by original position
    return [...localDocuments].sort((a, b) => {
      return (
        (positionMap[a.id] ?? Number.MAX_SAFE_INTEGER) -
        (positionMap[b.id] ?? Number.MAX_SAFE_INTEGER)
      );
    });
  }, [localDocuments]);

  // State for processing feedback
  const [processingState, setProcessingState] = useState<{
    isProcessing: boolean;
    lastUpdate: number;
    processedCount: number;
    totalCount: number;
    processingIds: string[];
    completedIds: string[];
    timeoutId?: NodeJS.Timeout;
  }>({
    isProcessing: false,
    lastUpdate: Date.now(),
    processedCount: 0,
    totalCount: 0,
    processingIds: [],
    completedIds: [],
  });

  // Automatically refetch pending documents at intervals when processing
  useEffect(() => {
    // Clear any existing timeout when component unmounts or deps change
    return () => {
      if (processingState.timeoutId) {
        clearTimeout(processingState.timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle automatic polling for document processing status
  const pollPendingDocuments = useCallback(() => {
    // Only poll if we have documents being processed
    if (processingState.processingIds.length === 0) {
      return;
    }

    // Create a polling function
    const poll = async () => {
      await refetchPendingDocuments();

      // Schedule next poll with decreasing frequency
      // Polling frequency slows down over time to avoid unnecessary requests
      const pollDelay = Math.min(5000, 1000 + processingState.lastUpdate / 10000);

      const timeoutId = setTimeout(poll, pollDelay);
      setProcessingState(prev => ({
        ...prev,
        timeoutId,
      }));
    };

    // Start polling
    poll();
  }, [processingState.processingIds, processingState.lastUpdate, refetchPendingDocuments]);

  // Effect to track processing state based on pending documents changes
  useEffect(() => {
    if (!pendingDocuments || pendingDocuments.length === 0) {
      // No pending documents, if we had some before, show completion notification
      if (processingState.isProcessing) {
        showNotification('All documents processed successfully', 'success');
      }

      // Reset processing state after a short delay
      const timeoutId = setTimeout(() => {
        setProcessingState(prev => ({
          ...prev,
          isProcessing: false,
          processingIds: [],
          completedIds: [],
          lastUpdate: Date.now(),
        }));
      }, 2000);

      // Store the timeout ID so we can clear it if needed
      setProcessingState(prev => ({
        ...prev,
        timeoutId,
      }));

      return;
    }

    // Get current processing and completed document IDs
    const currentProcessingIds = pendingDocuments
      .filter(doc => !doc.isprocessed)
      .map(doc => doc.id);

    const currentCompletedIds = pendingDocuments.filter(doc => doc.isprocessed).map(doc => doc.id);

    // Check if processing status has changed
    const hasNewCompleted = currentCompletedIds.some(
      id => !processingState.completedIds.includes(id)
    );

    const hasChangedProcessing =
      currentProcessingIds.length !== processingState.processingIds.length || hasNewCompleted;

    if (hasChangedProcessing) {
      // Show notification when processing status changes significantly
      if (!processingState.isProcessing && currentProcessingIds.length > 0) {
        showNotification(`Processing ${currentProcessingIds.length} documents...`, 'info');
      }

      // Update processing state with new information
      setProcessingState(prev => ({
        ...prev,
        isProcessing: currentProcessingIds.length > 0,
        lastUpdate: Date.now(),
        totalCount: pendingDocuments.length,
        processedCount: currentCompletedIds.length,
        processingIds: currentProcessingIds,
        completedIds: currentCompletedIds,
      }));

      // If we still have documents processing, ensure polling is active
      if (currentProcessingIds.length > 0) {
        pollPendingDocuments();
      }
    }
  }, [
    pendingDocuments,
    pollPendingDocuments,
    processingState.completedIds,
    processingState.processingIds,
    processingState.isProcessing,
    showNotification,
  ]);

  // Memoize document operations
  const handleDelete = useCallback(
    async (objectId: string) => {
      setDeletingId(objectId);
      try {
        await ObjectService.deleteObject(objectId);
        refreshDocumentGlobally();
      } catch (err) {
        console.error('Failed to delete document:', err);
      } finally {
        setDeletingId(null);
      }
    },
    [refreshDocumentGlobally]
  );

  const handleDeleteSelected = useCallback(() => {
    const deletableIds = selectedDocumentIds.filter(
      id => !documents?.find(doc => doc.id === id)?.hastakeoff
    );

    const nonDeletableIds = selectedDocumentIds.filter(
      id => documents?.find(doc => doc.id === id)?.hastakeoff
    );

    // Show confirmation dialog with details
    setDeleteConfirmDialog({
      open: true,
      itemsToDelete: deletableIds.length,
      hasTakeoffs: nonDeletableIds.length > 0,
      isProcessing: false,
    });
  }, [selectedDocumentIds, documents]);

  // Add the function to actually perform the deletion after confirmation
  const confirmDeleteSelected = useCallback(async () => {
    // Mark as processing to show loading state
    setDeleteConfirmDialog(prev => ({
      ...prev,
      isProcessing: true,
    }));

    // Get the deletable IDs (documents without takeoffs)
    const deletableIds = selectedDocumentIds.filter(
      id => !documents?.find(doc => doc.id === id)?.hastakeoff
    );

    // Delete each document
    for (const id of deletableIds) {
      try {
        setDeletingId(id);
        await ObjectService.deleteObject(id);
      } catch (err) {
        console.error(`Failed to delete document with ID ${id}:`, err);
      } finally {
        setDeletingId(null);
      }
    }

    // Clear selection and refresh the document list
    setSelectedDocumentIds([]);
    refreshDocumentGlobally();

    // Close the dialog and reset its state
    setDeleteConfirmDialog({
      open: false,
      itemsToDelete: 0,
      hasTakeoffs: false,
      isProcessing: false,
    });

    // Show success notification
    showNotification(`Successfully deleted ${deletableIds.length} documents`, 'success');
  }, [selectedDocumentIds, documents, refreshDocumentGlobally, showNotification]);

  const handleSelectAll = useCallback(() => {
    if (selectedDocumentIds.length === documents?.length) {
      setSelectedDocumentIds([]);
    } else {
      setSelectedDocumentIds(documents?.map(doc => doc.id) || []);
    }
  }, [selectedDocumentIds.length, documents]);

  const toggleDocumentSelection = useCallback((docId: string) => {
    setSelectedDocumentIds(prevSelected =>
      prevSelected.includes(docId)
        ? prevSelected.filter(id => id !== docId)
        : [...prevSelected, docId]
    );
  }, []);

  const handleEdit = useCallback((docId: string, currentName: string) => {
    setEditingId(docId);
    setUpdatedName(currentName);
  }, []);

  // Add a new function to cancel editing
  const cancelEditing = useCallback(() => {
    setEditingId(null);
  }, []);

  // Update the handleSave function to implement optimistic updates
  const handleSave = useCallback(
    async (docId: string, newName?: string) => {
      const nameToUse = newName || updatedName;

      // Save pending update
      pendingUpdatesRef.current[docId] = nameToUse;

      // Optimistically update the document name in the UI
      setLocalDocuments(prevDocs =>
        prevDocs.map(doc => (doc.id === docId ? { ...doc, objectname: nameToUse } : doc))
      );

      // Reset editing state
      setEditingId(null);

      try {
        // Perform the actual update in the backend
        await ObjectService.updateObjectName(docId, nameToUse);

        // Remove from pending updates on success
        delete pendingUpdatesRef.current[docId];

        // Refresh document lists across components
        refreshDocumentGlobally();
      } catch (err) {
        console.error('Failed to update document name:', err);

        // Remove from pending updates on error
        delete pendingUpdatesRef.current[docId];

        // Revert the failed update
        refetchObjects();
      }
    },
    [updatedName, refetchObjects, refreshDocumentGlobally]
  );

  // Cache preview URLs
  useEffect(() => {
    if (documents) {
      documents.forEach(doc => {
        if (!cachedPreviews.current[doc.id]) {
          cachedPreviews.current[doc.id] = doc.previewUrl;
        }
      });
    }
  }, [documents]);

  // Update handleApplySuggestion to refresh documents globally
  const handleApplySuggestion = useCallback(
    async (docId: string, gcsObjectName: string, suggestedName: string) => {
      try {
        // Save pending update
        pendingUpdatesRef.current[docId] = suggestedName;

        // Optimistically update the document name
        setLocalDocuments(prevDocs =>
          prevDocs.map(doc => (doc.id === docId ? { ...doc, objectname: suggestedName } : doc))
        );

        // Remove this suggestion from the list immediately
        setRenameSuggestions(prev => prev.filter(s => s.gcs_object_name !== gcsObjectName));

        // Apply the rename in the backend
        await ObjectService.updateObjectName(docId, suggestedName);
        showNotification('Document renamed successfully', 'success');

        // If no suggestions left, clean up and refresh
        if (renameSuggestions.length <= 1) {
          setRenameSuggestions([]);
        }

        // Remove from pending updates
        delete pendingUpdatesRef.current[docId];

        // Refresh document lists across components
        refreshDocumentGlobally();
      } catch (error) {
        console.error('Error applying rename suggestion:', error);
        showNotification('Failed to rename document', 'error');
        // Remove from pending updates on error
        delete pendingUpdatesRef.current[docId];
        // Revert the single failed update
        refetchObjects();
      }
    },
    [renameSuggestions.length, refetchObjects, showNotification, refreshDocumentGlobally]
  );

  // Direct action to reject a rename suggestion
  const handleRejectSuggestion = useCallback(
    (gcsObjectName: string) => {
      // Just remove from suggestions list
      setRenameSuggestions(prev => prev.filter(s => s.gcs_object_name !== gcsObjectName));
      showNotification('Rename suggestion rejected', 'info');

      // If no suggestions left, clean up
      if (renameSuggestions.length <= 1) {
        setRenameSuggestions([]);
      }
    },
    [renameSuggestions.length, showNotification]
  );

  // Update the useEffect that initializes localDocuments to respect pending updates
  useEffect(() => {
    if (documents && documents.length > 0) {
      // Apply any pending optimistic updates to the refreshed document list
      setLocalDocuments(
        documents.map(doc => {
          // If this document has a pending update, use the optimistic value
          if (pendingUpdatesRef.current[doc.id]) {
            return {
              ...doc,
              objectname: pendingUpdatesRef.current[doc.id],
            };
          }
          return doc;
        })
      );
    }
  }, [documents]);

  // Add state for the preview modal
  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    document: (typeof documents)[0] | null;
  }>({
    open: false,
    document: null,
  });

  // Function to open the preview modal
  const handleOpenPreview = useCallback((doc: (typeof documents)[0]) => {
    setPreviewModal({
      open: true,
      document: doc,
    });
  }, []);

  // Function to close the preview modal
  const handleClosePreview = useCallback(() => {
    setPreviewModal({
      open: false,
      document: null,
    });
  }, []);

  // Add state for modal editing
  const [modalEditMode, setModalEditMode] = useState(false);
  const [modalEditName, setModalEditName] = useState('');

  // Function to enable edit mode in the modal
  const enableModalEditMode = useCallback((initialName: string) => {
    setModalEditMode(true);
    setModalEditName(initialName);
  }, []);

  // Update saveModalEdit to use global refresh
  const saveModalEdit = useCallback(async () => {
    if (previewModal.document && modalEditName.trim()) {
      await handleSave(previewModal.document.id, modalEditName);

      // Update the preview modal document with the new name
      setPreviewModal(prev => ({
        ...prev,
        document: prev.document ? { ...prev.document, objectname: modalEditName } : null,
      }));

      setModalEditMode(false);

      // Refresh document lists across components
      refreshDocumentGlobally();
    }
  }, [previewModal, modalEditName, handleSave, refreshDocumentGlobally, setPreviewModal]);

  // Function to cancel modal edits
  const cancelModalEdit = useCallback(() => {
    setModalEditMode(false);
  }, []);

  // Reset modal edit state when the modal is closed
  useEffect(() => {
    if (!previewModal.open) {
      setModalEditMode(false);
    }
  }, [previewModal.open]);

  if (isLoading) return <CircularProgress />;
  if (error) {
    return (
      <Typography color="error">
        Error fetching documents: {(error as Error).message || 'Unknown error.'}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        position: 'relative',
        maxWidth: '100vw',
      }}
    >
      {/* Fixed Header Area */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.default,
          pt: 2,
          px: 2,
          pb: renameSuggestions.length > 0 ? 0 : 2,
          borderBottom:
            renameSuggestions.length > 0
              ? 'none'
              : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          width: '100%',
        }}
      >
        {/* Header with Actions Row - Consolidated UI */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: renameSuggestions.length > 0 ? 2 : 0,
            flexWrap: 'wrap',
            gap: 1,
            width: '100%',
          }}
        >
          {/* Left side: Title and document count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Documents
            </Typography>
            <Chip
              label={`${documents.length} total`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 24 }}
            />
          </Box>

          {/* Right side: Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Manual refresh button */}
            <Tooltip title="Refresh document list">
              <IconButton
                onClick={handleManualRefresh}
                aria-label="Refresh documents"
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                {documentStatus?.isProcessing ? (
                  <CircularProgress size={18} color="primary" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Selection actions */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Select all documents">
                <Checkbox
                  checked={selectedDocumentIds.length === documents.length}
                  indeterminate={
                    selectedDocumentIds.length > 0 && selectedDocumentIds.length < documents.length
                  }
                  onChange={handleSelectAll}
                  aria-label="Select all documents"
                  sx={{
                    color:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.7)
                        : theme.palette.text.secondary,
                    p: 1,
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              </Tooltip>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Always visible selection count chip */}
                <Chip
                  label={
                    selectedDocumentIds.length > 0
                      ? `${selectedDocumentIds.length} selected`
                      : 'Select documents'
                  }
                  size="small"
                  color={selectedDocumentIds.length > 0 ? 'primary' : 'default'}
                  variant={selectedDocumentIds.length > 0 ? 'filled' : 'outlined'}
                  sx={{
                    height: 24,
                    opacity: selectedDocumentIds.length > 0 ? 1 : 0.7,
                    minWidth: '120px',
                  }}
                />

                {/* Always visible Auto Rename button */}
                <Tooltip
                  title={
                    selectedDocumentIds.length > 0
                      ? 'Auto Rename Selected Documents'
                      : 'Select documents to use AI renaming'
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleRenameSelected}
                      disabled={renameProcessing || selectedDocumentIds.length === 0}
                      aria-label="Auto Rename Selected Documents"
                      size="small"
                      sx={{
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        },
                        opacity: selectedDocumentIds.length > 0 ? 1 : 0.6,
                      }}
                    >
                      {renameProcessing ? (
                        <CircularProgress size={18} />
                      ) : (
                        <AutoFixHighIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Always visible Delete button */}
                <Tooltip
                  title={
                    selectedDocumentIds.length > 0
                      ? 'Delete selected documents'
                      : 'Select documents to delete'
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleDeleteSelected}
                      disabled={selectedDocumentIds.length === 0}
                      aria-label="Delete selected documents"
                      size="small"
                      sx={{
                        color: theme.palette.error.main,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.2),
                        },
                        opacity: selectedDocumentIds.length > 0 ? 1 : 0.6,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Rename Suggestions and Actions - Combined in single row */}
        {renameSuggestions.length > 0 && (
          <Paper
            elevation={1}
            sx={{
              mb: 2,
              py: 1,
              px: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.info.dark, 0.1)
                  : alpha(theme.palette.info.light, 0.1),
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.info.dark, 0.3)
                  : alpha(theme.palette.info.light, 0.3)
              }`,
              borderRadius: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'center',
                  width: '100%',
                  my: 0.5,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={approveAllSuggestions}
                  disabled={applyingRenames}
                  startIcon={
                    applyingRenames ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />
                  }
                  sx={{ py: 0.5, height: 32, minWidth: '200px' }}
                >
                  {applyingRenames ? 'Applying...' : 'Apply All Suggestions'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={rejectAllSuggestions}
                  startIcon={<CancelOutlinedIcon />}
                  sx={{ py: 0.5, height: 32, minWidth: '160px' }}
                >
                  Dismiss All
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          pt: 0,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Active Uploads Indicator */}
        <DocumentManagerActiveUploads />

        {/* Document Table View */}
        <Paper
          elevation={1}
          sx={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Document Rows */}
          {sortedDocuments.map((doc, index) => {
            const isEditing = editingId === doc.id;
            const hasSuggestion = renameSuggestions.find(
              s => s.gcs_object_name === doc.gcsobjectname
            );
            const isEvenRow = index % 2 === 0;

            return (
              <Box
                key={doc.id}
                sx={{
                  display: 'flex',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? isEvenRow
                        ? '#2c2c2c'
                        : '#3c3c3c'
                      : isEvenRow
                        ? '#f5f5f5'
                        : '#e0e0e0',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                  transition: 'background-color 0.2s',
                  ...(hasSuggestion && {
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                  }),
                }}
              >
                {/* Selection Checkbox */}
                <Box
                  sx={{
                    width: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Checkbox
                    checked={selectedDocumentIds.includes(doc.id)}
                    onChange={() => toggleDocumentSelection(doc.id)}
                    sx={{
                      color: theme.palette.text.secondary,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {/* Preview Thumbnail with Expand Option */}
                <Box sx={{ width: 180, p: 1, position: 'relative' }}>
                  <Box
                    sx={{
                      height: 100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#fafafa',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        '& .preview-overlay': {
                          opacity: 1,
                        },
                      },
                    }}
                    onClick={() => handleOpenPreview(doc)}
                  >
                    <PDFPreview
                      doc={doc}
                      cachedPreviews={cachedPreviews}
                      transformRefs={transformRefs}
                    />

                    {/* Overlay for preview expansion */}
                    <Box
                      className="preview-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        borderRadius: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label="Expand preview"
                        sx={{
                          backgroundColor: alpha(theme.palette.common.white, 0.9),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.common.white, 1),
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                {/* Document Name and Editing */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: 1,
                    position: 'relative',
                  }}
                >
                  {isEditing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={updatedName}
                        onChange={e => setUpdatedName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleSave(doc.id);
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        variant="outlined"
                        size="small"
                        autoFocus
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.default, 0.1)
                                : alpha(theme.palette.background.paper, 0.7),
                          },
                        }}
                      />
                      <Tooltip title="Save (Enter)">
                        <IconButton onClick={() => handleSave(doc.id)} color="primary" size="small">
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel (Esc)">
                        <IconButton onClick={cancelEditing} size="small">
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{
                          color: theme.palette.text.primary,
                          '&:hover': { color: theme.palette.primary.main },
                          cursor: 'pointer',
                        }}
                        onDoubleClick={() => handleEdit(doc.id, doc.objectname)}
                      >
                        {doc.objectname}
                      </Typography>

                      {/* AI Rename Suggestion */}
                      {hasSuggestion && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.dark, 0.1)
                                : alpha(theme.palette.primary.light, 0.1),
                            border: `1px solid ${
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.dark, 0.3)
                                : alpha(theme.palette.primary.light, 0.3)
                            }`,
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <Typography variant="body2" color="primary" fontWeight={500}>
                              {`${hasSuggestion.sheet_number} - ${hasSuggestion.sheet_name}`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Apply Suggestion">
                              <IconButton
                                size="small"
                                color="primary"
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  const newName = `${hasSuggestion.sheet_number} - ${hasSuggestion.sheet_name}`;
                                  handleApplySuggestion?.(doc.id, doc.gcsobjectname, newName);
                                }}
                              >
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Dismiss">
                              <IconButton
                                size="small"
                                color="error"
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  handleRejectSuggestion?.(doc.gcsobjectname);
                                }}
                              >
                                <CancelOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Actions */}
                <Box
                  sx={{
                    width: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 1,
                  }}
                >
                  <Tooltip title="Edit Name">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(doc.id, doc.objectname)}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      doc.hastakeoff ? 'Cannot delete document with takeoff' : 'Delete document'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === doc.id || doc.hastakeoff}
                        onClick={() => handleDelete(doc.id)}
                        sx={{
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.2) },
                        }}
                      >
                        {deletingId === doc.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}

          {/* Empty State */}
          {sortedDocuments.length === 0 && (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                color: theme.palette.text.secondary,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="body1">No documents found</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Document Preview Modal */}
      <Modal
        open={previewModal.open}
        onClose={handleClosePreview}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={previewModal.open}>
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
              boxShadow: theme.shadows[20],
              borderRadius: 2,
              width: '90%',
              maxWidth: 1200,
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                handleClosePreview();
              }
            }}
            tabIndex={0}
          >
            {/* Modal Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                py: 1.5,
                px: 2,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.default, 0.8),
              }}
            >
              {modalEditMode ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <TextField
                    value={modalEditName}
                    onChange={e => setModalEditName(e.target.value)}
                    placeholder="Document name"
                    fullWidth
                    size="small"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        saveModalEdit();
                      } else if (e.key === 'Escape') {
                        cancelModalEdit();
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.default, 0.1)
                            : alpha(theme.palette.background.paper, 0.7),
                      },
                    }}
                  />
                  <Tooltip title="Save (Enter)">
                    <IconButton onClick={saveModalEdit} color="primary" size="small">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel (Esc)">
                    <IconButton onClick={cancelModalEdit} size="small">
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Typography variant="h6" fontWeight={500}>
                  {previewModal.document?.objectname || 'Document Preview'}
                </Typography>
              )}
              <IconButton
                onClick={handleClosePreview}
                size="small"
                aria-label="Close preview"
                sx={{
                  color: theme.palette.text.primary,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Modal Content - Large PDF Preview */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.mode === 'dark' ? '#222222' : '#f0f0f0',
              }}
            >
              {previewModal.document && (
                <Box
                  sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <PDFPreview
                    doc={previewModal.document}
                    cachedPreviews={cachedPreviews}
                    transformRefs={transformRefs}
                    enableZoom={true}
                  />
                </Box>
              )}
            </Box>

            {/* Modal Footer */}
            <Box
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                py: 1.5,
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.default, 0.8),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {previewModal.document && (
                  <>
                    {/* Edit button */}
                    <Tooltip title="Edit Name">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          if (previewModal.document) {
                            enableModalEditMode(previewModal.document.objectname);
                          }
                        }}
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Delete button */}
                    <Tooltip
                      title={
                        previewModal.document?.hastakeoff
                          ? 'Cannot delete document with takeoff'
                          : 'Delete document'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={
                            deletingId === previewModal.document?.id ||
                            previewModal.document?.hastakeoff
                          }
                          onClick={() => {
                            if (previewModal.document) {
                              handleDelete(previewModal.document.id);
                              handleClosePreview();
                            }
                          }}
                          sx={{
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.2) },
                          }}
                        >
                          {deletingId === previewModal.document?.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* AI rename suggestion info */}
                    {renameSuggestions.find(
                      s => s.gcs_object_name === previewModal.document?.gcsobjectname
                    ) && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          ml: 1,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                      >
                        <SmartToyIcon color="primary" sx={{ fontSize: 16 }} />
                        <Typography variant="body2" color="primary">
                          AI rename suggestion available
                        </Typography>
                      </Box>
                    )}

                    {/* Conditional AI rename button */}
                    {previewModal.document &&
                      renameSuggestions.some(
                        s => s.gcs_object_name === previewModal.document?.gcsobjectname
                      ) && (
                        <Box
                          sx={{
                            ml: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => {
                              if (previewModal.document) {
                                const suggestion = renameSuggestions.find(
                                  s => s.gcs_object_name === previewModal.document?.gcsobjectname
                                );
                                if (suggestion) {
                                  const newName = `${suggestion.sheet_number} - ${suggestion.sheet_name}`;
                                  handleApplySuggestion(
                                    previewModal.document.id,
                                    previewModal.document.gcsobjectname,
                                    newName
                                  );
                                  handleClosePreview();
                                }
                              }
                            }}
                          >
                            Apply Rename
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (previewModal.document) {
                                handleRejectSuggestion(previewModal.document.gcsobjectname);
                                handleClosePreview();
                              }
                            }}
                          >
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                  </>
                )}
              </Box>
              <Button
                variant="outlined"
                onClick={handleClosePreview}
                startIcon={<CloseIcon />}
                size="small"
                sx={{
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.main
                      : theme.palette.primary.main,
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.main
                      : theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Add Snackbar notification system */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() =>
          !deleteConfirmDialog.isProcessing &&
          setDeleteConfirmDialog(prev => ({ ...prev, open: false }))
        }
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {deleteConfirmDialog.itemsToDelete > 0
            ? 'Confirm Document Deletion'
            : 'Unable to Delete Documents'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {deleteConfirmDialog.itemsToDelete > 0 ? (
              <>
                You are about to delete <strong>{deleteConfirmDialog.itemsToDelete}</strong>{' '}
                document{deleteConfirmDialog.itemsToDelete !== 1 ? 's' : ''}. This action cannot be
                undone.
                {deleteConfirmDialog.hasTakeoffs && (
                  <Box sx={{ mt: 2, color: theme.palette.warning.main }}>
                    <Typography variant="body2">
                      Note: {selectedDocumentIds.length - deleteConfirmDialog.itemsToDelete}{' '}
                      document
                      {selectedDocumentIds.length - deleteConfirmDialog.itemsToDelete !== 1
                        ? 's'
                        : ''}{' '}
                      with takeoffs cannot be deleted.
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography color="error">
                None of the selected documents can be deleted because they all have takeoffs.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog(prev => ({ ...prev, open: false }))}
            color="primary"
            disabled={deleteConfirmDialog.isProcessing}
          >
            Cancel
          </Button>
          {deleteConfirmDialog.itemsToDelete > 0 && (
            <Button
              onClick={confirmDeleteSelected}
              color="error"
              variant="contained"
              disabled={deleteConfirmDialog.isProcessing}
              startIcon={
                deleteConfirmDialog.isProcessing ? <CircularProgress size={16} /> : <DeleteIcon />
              }
            >
              {deleteConfirmDialog.isProcessing ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
